import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, X, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerificationBanner({ token, userRole, onVerifyClick }) {
  const [status, setStatus]   = useState(null);  // { verification_status, grace_days_remaining }
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/verification/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.grace_days_remaining !== undefined) setStatus(d); })
      .catch(() => {});
  }, [token]);

  if (!status) return null;
  if (status.verification_status === 'verified') return null;
  if (dismissed && status.grace_days_remaining > 3) return null;

  const days  = status.grace_days_remaining;
  const isUrgent    = days <= 3 && days > 0;
  const isExpired   = days === 0;
  const isReview    = status.verification_status === 'under_review';
  const isRejected  = status.verification_status === 'rejected';

  // Under review — show quiet status
  if (isReview) return (
    <div className="mx-6 mt-4 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3">
      <Shield className="w-4 h-4 text-blue-500 flex-shrink-0" />
      <p className="text-sm text-blue-700 flex-1">
        Your ID is under review — we&apos;ll verify within 24 hours and award your 500 pt bonus.
      </p>
    </div>
  );

  // Rejected — show resubmit prompt
  if (isRejected) return (
    <div className="mx-6 mt-4 flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
      <p className="text-sm text-red-700 flex-1">
        Your ID verification was unsuccessful. Please resubmit with a clearer document.
      </p>
      <button onClick={() => onVerifyClick?.()}
        className="flex-shrink-0 text-xs font-bold text-white px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 transition-colors">
        Resubmit
      </button>
    </div>
  );

  // Full-screen block on day 0
  if (isExpired) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(4,30,43,0.95)', backdropFilter: 'blur(8px)' }}>
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
          <Shield className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-2xl font-black text-gray-900 mb-3">Verification Required</h2>
        <p className="text-gray-500 text-sm mb-6 leading-relaxed">
          Your free access period has ended. Verify your ID to continue using RentalX — it takes under 2 minutes and earns you 500 bonus points.
        </p>
        <button onClick={() => onVerifyClick?.()}
          className="w-full py-3.5 rounded-2xl font-bold text-white hover:opacity-90 transition-opacity"
          style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
          Verify My ID Now · Earn 500 pts
        </button>
      </div>
    </div>
  );

  // Grace period banner
  const bgColor = isUrgent ? 'bg-amber-50 border-amber-300' : 'bg-sky-50 border-sky-200';
  const textColor = isUrgent ? 'text-amber-800' : 'text-sky-800';
  const icon = isUrgent ? <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" /> : <Shield className="w-4 h-4 text-sky-500 flex-shrink-0" />;

  return (
    <div className={`mx-6 mt-4 flex items-center gap-3 border rounded-2xl px-4 py-3 ${bgColor}`}>
      {icon}
      <p className={`text-sm flex-1 ${textColor}`}>
        {isUrgent
          ? `⚠️ ${days} day${days === 1 ? '' : 's'} of free access remaining — verify now to keep full access and earn 500 pts`
          : `🔓 ${days} days of free access remaining — verify your ID to unlock your Verified badge and earn 500 bonus points`
        }
      </p>
      <button onClick={() => onVerifyClick?.()}
        className="flex-shrink-0 text-xs font-bold text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
        style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
        Verify
      </button>
      {!isUrgent && (
        <button onClick={() => setDismissed(true)} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
